<script setup lang="ts">
import { computed, h, onMounted, ref } from 'vue';
import { hebeClient, mateBackendClient } from '@/api/client';
import { $alert, $message } from '@nio-fe/lego';
import useGlobalStore from '@/store/globalStore';
import { removeCacheDB } from '@/utils/revalidate_cache';
import FailedMsgs from '@/components/AllowBindAccountDialog/FailedMsgs.vue';

const dialogVisible = ref(false);
const brandActionMapping = ref<
  Record<
    string,
    {
      isNeedChangeNickname: boolean;
      accountId?: string;
      brand: string;
      nickname: string;
      targetNickname?: string;
      shouldBind: boolean;
    }
  >
>({});
const checkBind = ref<boolean[] | undefined>(undefined);
const config = ref<any>({
  paragraph1:
    '尊敬的工作伙伴,\n为了更好地支持您的日常工作，我们将为您创建以下品牌的账号：',
  shouldChangeNickname:
    '同时,基于您的工作岗位要求,系统将为您自动进行用户端App的昵称实名和员工身份标识:',
  brands: {
    nio: '蔚来',
    alps: '乐道',
    fy: '萤火虫',
  },
  paragraph2:
    '请确认以下信息：\n1. 您已阅读并理解所有相关的用户协议和隐私政策。\n2. 您同意公司使用您的个人信息以完成账号注册和后续服务。\n3. 您授权公司将您的账号信息同步至上述品牌的MATE系统中。',
  checkboxes: {
    nio: {
      text: '《蔚来 工作人员个人信息保护声明》',
      url: 'https://nio.feishu.cn/docx/I74Odgp5wosqwBxXHw9cscP9nBc',
    },
    alps: {
      text: '《乐道 工作人员个人信息保护声明》',
      url: 'https://nio.feishu.cn/docx/P8fvdyP8uoj0AwxoJdyc3CWInaf',
    },
    fy: {
      text: '《萤火虫 工作人员个人信息保护声明》',
      url: 'https://nio.feishu.cn/docx/Y6Mdd0VGYoPiInxOKvpcgqHonjl',
    },
  },
  disclaimer: '',
});

const shouldChangeAndyNickname = computed(() => {
  return Object.values(brandActionMapping.value).some(
    (i) => i.isNeedChangeNickname
  );
});

const shouldBindAnyBrand = computed(() => {
  return Object.values(brandActionMapping.value).some((i) => i.shouldBind);
});

// onMounted(async () => {
//   const env = ['dev', 'test', 'stg'].includes(globalStore.userInfo.env)
//     ? 'test'
//     : 'prod';
//   await fetch(
//     `https://static.nio.com/fx-static/1547/fixed/allow-bind-account-config.${env}.json?t=${Date.now()}`
//   )
//     .then((r) => r.json())
//     .then((data) => {
//       config.value = data;
//     });
// });

const handleSubmit = async () => {
  if (shouldBindAnyBrand.value) {
    if (!checkBind.value || !checkBind.value[0]) {
      checkBind.value = [];
      return;
    }
  }
  if (!shouldBindAnyBrand.value && !shouldChangeAndyNickname.value) {
    return;
  }
  const brandActions = Object.values(brandActionMapping.value).reduce<
    {
      brand: string;
      accountId?: string;
      actions: string;
    }[]
  >((r, i) => {
    if (i.shouldBind || i.isNeedChangeNickname) {
      r.push({
        brand: i.brand,
        accountId: i.accountId,
        actions: [
          i.shouldBind ? 'register_bind' : undefined,
          !i.shouldBind && i.isNeedChangeNickname
            ? 'change_nickname'
            : undefined,
        ]
          .filter((i) => !!i)
          .join(','),
      });
    }
    return r;
  }, []);
  try {
    const result = await hebeClient.registerAction(brandActions);
    const failed = result.filter((i) => i.isBind === false);
    const succeed = result.filter((i) => !!i.isBind);
    const failedMsg = failed.map(
      (i) => `${config.value?.brands[i.brand]}: ${i.bindFailMsg}`
    );
    if (failedMsg.length) {
      await $alert(
        h(FailedMsgs, {
          errorMsgs: failedMsg,
        }),
        '品牌账号注册失败',
        {
          type: 'error',
        }
      );
    }
    if (succeed.length) {
      $message.success(
        `${succeed
          .map((i) => config.value?.brands[i.brand])
          .join(',')}账号注册成功`
      );
      await removeCacheDB();
      window.location.reload();
    }
    /*TODO: 修改昵称应该有个提示，预期下次迭代添加*/
    dialogVisible.value = false;
  } catch (e) {
    $message.error('注册失败，请重试');
  }
};

const getBrandNicknameDesc = (brand: string) => {
  let desc = `${config.value.brands[brand]}`;
  if (brandActionMapping.value[brand]?.isNeedChangeNickname) {
    const { nickname, targetNickname } = brandActionMapping.value[brand];
    if (targetNickname && nickname) {
      desc = desc + `：从${nickname}调整为${targetNickname}`;
    } else {
      desc = desc + `：调整为${targetNickname}`;
    }
  }
  return desc;
};

defineExpose({
  async checkAndIfOpenDialog() {
    const registerBind = await hebeClient.queryBrandBind();
    brandActionMapping.value = registerBind.reduce((r, i) => {
      r[i.brand] = {
        ...i,
        shouldBind: !i.accountId,
      };
      return r;
    }, {});
    if (shouldBindAnyBrand.value || shouldChangeAndyNickname.value) {
      dialogVisible.value = true;
    }
  },
  closeDialog() {
    dialogVisible.value = false;
  },
});
</script>

<template>
  <n-dialog
    v-model="dialogVisible"
    title="多品牌账号注册"
    primary-text="同意并注册"
    secondary-text="取消"
    @primary-click="handleSubmit"
  >
    <div class="bind-account">
      <p class="bind-account__paragraph" v-html="config.paragraph1"></p>
      <p
        v-if="shouldChangeAndyNickname"
        v-html="config.shouldChangeNickname"
        class="bind-account__paragraph"
      ></p>
      <div class="bind-account__brand-list">
        <ul>
          <template v-for="(brandText, brand) in config.brands" :key="brand">
            <li
              class="bind-account__brand-item"
              v-if="
                brandActionMapping[brand]?.shouldBind ||
                brandActionMapping[brand]?.isNeedChangeNickname
              "
              style="list-style: disc; margin-left: 22px"
            >
              {{ getBrandNicknameDesc(brand as string) }}
            </li>
          </template>
        </ul>
      </div>
      <template v-if="shouldBindAnyBrand">
        <p class="bind-account__paragraph" v-html="config.paragraph2"></p>
        <div class="bind-account__checkbox-wrapper">
          <n-checkbox
            v-model="checkBind"
            :true-label="true"
            :false-label="false"
            >同意以下</n-checkbox
          >
          <p
            v-if="checkBind && checkBind[0] === undefined"
            class="bind-account__check-required"
          >
            *请勾选“同意”进行后续操作
          </p>
          <template v-for="(checkbox, brand) in config.checkboxes" :key="brand">
            <a
              v-if="brandActionMapping[brand]?.shouldBind"
              :href="checkbox.url"
              target="_blank"
              class="bind-account__link"
            >
              {{ checkbox.text }}
            </a>
          </template>
        </div>
      </template>

      <p class="bind-account__disclaimer" v-html="config.disclaimer"></p>
    </div>
  </n-dialog>
</template>

<style scoped lang="scss">
.bind-account {
  &__paragraph {
    font-size: 14px;
    line-height: 22px;
    margin-bottom: 22px;
    white-space: pre-line;
  }
  &__check-required {
    color: #e83030;
    margin-left: 24px;
  }
  &__brand-list {
    margin-bottom: 32px;
  }
  &__brand-item {
    list-style: disc;
    margin-left: 22px;
  }
  &__disclaimer {
    font-size: 12px;
    color: #999dae;
    margin-top: 20px;
  }
  &__link {
    font-size: 14px;
    color: #178ca6;
    margin-left: 24px;
    display: block;
  }
}
</style>
